import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { BrandMenu } from "./BrandMenu";
import { theme } from "./theme";

// ── Floodlight-style page shell ──────────────────────────────

const Outer = styled.div`
  padding-top: env(safe-area-inset-top);
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
`;

const Inner = styled.div<{ wide?: boolean }>`
  max-width: ${(p) => (p.wide ? "64rem" : "32rem")};
  width: 100%;
  height: 100svh;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem 0;

  ${theme.bp.sm} {
    padding: 1.5rem 1.5rem 0;
  }
`;

const Header = styled.header`
  flex-shrink: 0;
  margin-bottom: 1.25rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

const TitleText = styled.h1`
  font-family: ${theme.fonts.display};
  font-size: 1.5rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 0.75rem;
  color: ${theme.colors.textMuted};
  margin-top: 0.375rem;
`;

const Main = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  isolation: isolate;
  padding-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  scrollbar-width: thin;
  scrollbar-color: color-mix(in oklab, ${theme.colors.textMuted} 25%, transparent) transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: color-mix(in oklab, ${theme.colors.textMuted} 25%, transparent);
    border-radius: ${theme.radii.full};
  }
`;

export const PageShell = ({
  icon,
  title,
  subtitle,
  wide,
  children,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  wide?: boolean;
  children: ReactNode;
}) => (
  <Outer>
    <Inner wide={wide}>
      <Header>
        <HeaderRow>
          <TitleWrap>
            <BrandMenu icon={icon} />
            <TitleText>{title}</TitleText>
          </TitleWrap>
        </HeaderRow>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Header>
      <Main>{children}</Main>
    </Inner>
  </Outer>
);
